import {
  integrationFieldValueSchema,
  isCustomFieldValue,
  type IntegrationFieldDescriptor,
  type IntegrationProviderStatusDto,
  type WriteIntegrationConfigRequest,
} from '@aysar/contracts';
import { z, type ZodType } from 'zod';

import { type CreateVerdict } from '@/shared/components/selection/types/selection-field.types';
import {
  type FieldColSpan,
  type FieldDef,
} from '@/shared/components/dynamic-form/types/dynamic-form.types';

/** Translates a key, with interpolation. The caller's `t`. */
export type Translate = (key: string, params?: Record<string, unknown>) => string;

/**
 * A field descriptor's form path.
 *
 * Dot-free by construction, because a field key is `lowerCamel` — but routed
 * through one function anyway, so the form, the schema and the submit mapping can
 * never disagree about it.
 */
export function fieldPath(field: IntegrationFieldDescriptor): string {
  return field.key;
}

/**
 * A field's translated name.
 *
 * The catalogue names fields by KEY (`field.label.apiKey`) rather than by
 * capability + key, because a field key means the same thing wherever it appears —
 * an `apiKey` is an API key for Gemini and for RevenueCat alike — and one entry per
 * distinct key is what keeps a new provider free of new copy.
 */
export function fieldLabel(field: IntegrationFieldDescriptor, t: Translate): string {
  const key = `field.label.${field.key}`;
  const translated = t(key);

  return translated === key ? field.key : translated;
}

/**
 * The fields that carry their own help line under the control.
 *
 * A **listed** const rather than a `translated === key` probe, so the family
 * `field.hint.<key>` has a completeness assertion (the spec checks both
 * catalogs carry every entry) — the orphan guard cannot see a composed key, and
 * P24d2 is what a composed family with no check of its own looks like. The
 * SMTP three are the fields an operator most often has wrong: `secure` (465 vs
 * 587/2525), `port`, and `from` (the brand name is added as the display name);
 * the SMS four (P30b1) say where each vendor console hides the value.
 */
export const FIELD_HINT_KEYS = [
  'secure',
  'port',
  'from',
  'appSid',
  'senderId',
  'verifyServiceSid',
  'codeLength',
  'templateIdAr',
  'templateIdEn',
] as const;

type FieldHintKey = (typeof FIELD_HINT_KEYS)[number];

function hasOwnHint(key: string): key is FieldHintKey {
  return (FIELD_HINT_KEYS as readonly string[]).includes(key);
}

/**
 * The help line under a control: the field's own hint where it has one, the
 * type's generic line ("A number", "On or off") everywhere else.
 */
export function fieldDescription(field: IntegrationFieldDescriptor, t: Translate): string {
  return hasOwnHint(field.key) ? t(`field.hint.${field.key}`) : t(`field.${field.type}`);
}

/**
 * One catalog field → one control (N21: the form is generated, never written).
 *
 * A **secret** is a password field whose placeholder is its own mask, and it is
 * never `isRequired` even when the descriptor says so: the api's blank-keeps-secret
 * rule means an empty box is "leave the stored value alone", and marking it
 * required would demand the operator retype a credential they cannot read.
 */
export function providerFieldFor(
  field: IntegrationFieldDescriptor,
  status: IntegrationProviderStatusDto | undefined,
  t: Translate,
): FieldDef {
  const current = status?.fields.find((entry) => entry.key === field.key);
  // Typed rather than inferred: a bare ternary widens to `string | number`,
  // which is not the `FieldColSpan` union every field def expects.
  const colSpan: FieldColSpan =
    field.type === 'text' || field.type === 'url' || field.type === 'json' ? 'full' : 1;
  const base = {
    name: fieldPath(field),
    // Translated (U8), never the raw key: `serviceAccountJson` is not a label in
    // any language. A field the catalogue has not named yet falls back to its key
    // rather than rendering blank — visible debt, not a missing control.
    label: fieldLabel(field, t),
    description: fieldDescription(field, t),
    // A URL and a free-text credential are long values; a key, a number, a
    // switch or a model id is not, so only these two take the whole row.
    colSpan,
  };

  const storedLast4 = current?.last4 ?? null;

  // A secret is a masked one-liner — EXCEPT a `json` one, which is a document.
  // This branch runs before the type switch, and swallowing `json` here is what
  // put FCM's ~2 KB service account in a single masked input, where a truncated
  // paste is undetectable until push delivery fails in production.
  if (field.secret && field.type !== 'json') {
    return {
      ...base,
      kind: 'password',
      description:
        storedLast4 === null
          ? t('field.secretEmpty')
          : t('field.secretStored', { last4: storedLast4 }),
      placeholder: t('field.secretPlaceholder'),
    };
  }

  // EXHAUSTIVE, with a `never` default. It used to end in an unconditional
  // `kind: 'text'`, so a field type nobody had mapped yet degraded silently into
  // a free-text box — the precise failure this phase exists to remove from the
  // AI model field, reintroduced one catalogue entry later.
  switch (field.type) {
    case 'boolean':
      return { ...base, kind: 'switch' };

    case 'number':
      // The descriptor's bounds reach the native input (P30b1): a code length
      // outside what the phone can draw is refused before it is typed.
      return {
        ...base,
        kind: 'number',
        isRequired: field.required,
        ...(field.min === undefined ? {} : { min: field.min }),
        ...(field.max === undefined ? {} : { max: field.max }),
      };

    case 'select':
      return {
        ...base,
        kind: 'combobox',
        options: (field.options ?? []).map((value) => ({ value, label: value })),
        // The list is curation, not law (see `integrationFieldValueSchema`), so
        // the escape is always offered — and it is the ONLY way to type a value
        // outside it, which is what makes going off-catalogue a decision.
        isCreatable: true,
        validateCreate: (rawTerm) => validateCustomValue(field, rawTerm, t),
        isRequired: field.required,
        description:
          current?.value !== undefined &&
          current.value !== null &&
          isCustomFieldValue(field, current.value)
            ? t('field.selectCustom', { value: current.value })
            : t('field.select'),
      };

    case 'json':
      return {
        ...base,
        kind: 'json',
        rows: 10,
        // It opens BLANK — the stored document never left the server (N21) — so
        // this description is the only place the stored one is acknowledged,
        // and the only warning that a paste replaces rather than edits it.
        //
        // Deliberately WITHOUT `last4`, unlike every other secret: the last four
        // characters of a JSON document are its closing brace and quote, so
        // "ending ••••en\"}" identifies nothing while looking like a truncated
        // value. Found on the real screen — the api stores it, we just refuse to
        // dress noise up as provenance.
        description: !field.secret
          ? t('field.json')
          : storedLast4 === null
            ? t('field.jsonSecretEmpty')
            : t('field.jsonSecretStored'),
        // Never required, for the same reason no secret is: an untouched editor
        // means "keep what is stored", and the server is the only party that
        // knows whether anything is.
      };

    case 'text':
    case 'password':
      return { ...base, kind: 'text', isRequired: field.required };

    case 'url':
      return { ...base, kind: 'text', inputType: 'url', isRequired: field.required };

    case 'email':
      return { ...base, kind: 'text', inputType: 'email', isRequired: field.required };

    default: {
      const unmapped: never = field.type;
      throw new Error(`No control is mapped for the integration field type "${String(unmapped)}".`);
    }
  }
}

/**
 * The verdict on a value an operator typed rather than picked.
 *
 * Runs the **contract's own** schema rather than restating its rules, so the
 * create affordance and the write the server accepts cannot disagree (U3). The
 * duplicate check is ours because `useSelectionFilter` hands the whole decision
 * over once a `validateCreate` is supplied.
 */
function validateCustomValue(
  field: IntegrationFieldDescriptor,
  rawTerm: string,
  t: Translate,
): CreateVerdict {
  const value = rawTerm.trim();

  if (field.options?.includes(value) === true) {
    return { status: 'rejected', reason: t('field.selectDuplicate', { value }) };
  }

  const parsed = integrationFieldValueSchema(field).safeParse(value);

  return parsed.success
    ? { status: 'ok', value }
    : { status: 'rejected', reason: t('field.selectInvalid') };
}

/**
 * The form schema for one provider's configuration.
 *
 * Required-ness is deliberately **not** enforced on secrets, for the same reason
 * they are never marked required: blank means "keep what is stored", and the server
 * — which is the only party that knows whether something is stored — is the one
 * that decides whether the result is complete.
 */
export function providerFormSchema(
  fields: readonly IntegrationFieldDescriptor[],
  t: Translate,
): ZodType<Record<string, unknown>, Record<string, unknown>> {
  const shape: Record<string, ZodType> = {};

  for (const field of fields) {
    if (field.type === 'json') {
      // The editor hands over a PARSED value, and not everything that parses is
      // a credential: `"hello"`, `42` and `[…]` are all valid JSON. Requiring a
      // plain object is what stops a write that would replace a live service
      // account with something the operator pasted by accident. Optional,
      // because an untouched editor still means "keep what is stored".
      //
      // A `custom` issue rather than `z.looseObject`, because the kit maps Zod's
      // own `invalid_type` to "this field is required" — which is the wrong
      // sentence entirely for a document of the wrong shape.
      shape[fieldPath(field)] = z
        .unknown()
        .optional()
        .superRefine((value, ctx) => {
          if (value === undefined) return;

          if (typeof value !== 'object' || value === null || Array.isArray(value)) {
            ctx.addIssue({ code: 'custom', message: t('field.jsonInvalidShape') });
          }
        });
      continue;
    }

    if (field.secret) {
      shape[fieldPath(field)] = z.string().optional();
      continue;
    }

    switch (field.type) {
      case 'boolean':
        shape[fieldPath(field)] = z.boolean().optional();
        break;
      case 'number': {
        // The same bounds the api applies, so the refusal is translated and
        // beside the box rather than a 422 after the save.
        const rangeMessage = t('field.numberRange', { min: field.min, max: field.max });
        let bounded = z.number();
        if (field.min !== undefined) bounded = bounded.min(field.min, rangeMessage);
        if (field.max !== undefined) bounded = bounded.max(field.max, rangeMessage);
        shape[fieldPath(field)] = field.required
          ? bounded
          : bounded.optional().or(z.literal('').transform(() => undefined));
        break;
      }
      case 'email':
        shape[fieldPath(field)] = field.required ? z.email() : z.string().optional();
        break;
      case 'url':
        shape[fieldPath(field)] = field.required ? z.url() : z.string().optional();
        break;
      case 'select':
      case 'text':
      case 'password':
        // A select is a bounded string here too, NOT `z.enum(field.options)`:
        // the form validates the whole object, so an enum would make a config
        // whose stored value has left the catalogue impossible to edit at all —
        // including the API key beside it.
        shape[fieldPath(field)] = field.required ? z.string().min(1) : z.string().optional();
        break;
      default: {
        // Exhaustive: it used to be `default: z.string()`, so a new field type
        // silently validated as free text no matter what the contract said.
        const unmapped: never = field.type;
        throw new Error(`No form schema is mapped for the field type "${String(unmapped)}".`);
      }
    }
  }

  return z.object(shape) as unknown as ZodType<Record<string, unknown>, Record<string, unknown>>;
}

/**
 * The form's values → the api's write body.
 *
 * A **blank secret is dropped entirely**, because sending `''` would mean "clear
 * it" for a plain field and the api distinguishes the two by field, not by value.
 * Sending nothing is the only spelling of "leave this credential alone" — and it is
 * also why an operator can change a bucket name without re-entering a key.
 */
export function toWriteConfigBody(
  fields: readonly IntegrationFieldDescriptor[],
  values: Record<string, unknown>,
): WriteIntegrationConfigRequest {
  const out: Record<string, string | number | boolean | null> = {};

  for (const field of fields) {
    const value = values[fieldPath(field)];

    if (field.secret && (value === undefined || value === '')) continue;

    if (value === undefined) {
      out[field.key] = null;
      continue;
    }

    // A `json` field's value is the PARSED document; the vault stores a string.
    // Without this it falls into the object branch below and is sent as `null` —
    // i.e. saving the form after pasting a valid service account would CLEAR the
    // credential the operator was in the middle of setting.
    if (field.type === 'json') {
      out[field.key] = JSON.stringify(value);
      continue;
    }

    out[field.key] =
      typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? value
        : null;
  }

  return { values: out };
}

/**
 * A stored document, as the editor wants it: parsed, or nothing.
 *
 * Never a throw — a value the vault holds but cannot be parsed is a fact about
 * the deployment, not a reason to fail a render. The editor opens empty and the
 * stored text stands until something is saved over it.
 */
function parseStored(value: string | number | boolean | null | undefined): unknown {
  if (typeof value !== 'string' || value === '') return undefined;

  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

/** The current values a config form opens with — secrets always blank (N21). */
export function providerInitialValues(
  fields: readonly IntegrationFieldDescriptor[],
  status: IntegrationProviderStatusDto | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const field of fields) {
    if (field.type === 'json') {
      // A SECRET document opens empty — its value never left the server (N21) —
      // and `''` would be wrong even then, because `JsonEditor`'s value is the
      // PARSED document and `''` pretty-prints as the JSON string `""`, a value
      // nobody entered sitting in the box that replaces a live credential.
      //
      // A plain document is different: it CAN be read back, so opening it empty
      // would let the mere act of opening the form and pressing Save clear it
      // (`undefined` → `null` in `toWriteConfigBody`) — the null-write this
      // phase exists to remove.
      //
      // One case this does NOT rescue, stated rather than glossed: stored text
      // that will not parse opens empty (the editor's value is the parsed
      // document, so handing it a broken string would throw during render), and
      // the next Save therefore clears it. The catalogue declares no non-secret
      // `json` field today; see PROGRESS §Known issues before adding one.
      out[fieldPath(field)] = field.secret
        ? undefined
        : parseStored(status?.fields.find((entry) => entry.key === field.key)?.value);
      continue;
    }

    if (field.secret) {
      // There is nothing to prefill: the value never left the server.
      out[fieldPath(field)] = '';
      continue;
    }

    const current = status?.fields.find((entry) => entry.key === field.key);
    out[fieldPath(field)] =
      current?.value ??
      (field.type === 'boolean' ? false : field.type === 'number' ? undefined : '');
  }

  return out;
}
